/**
 * Helpers for Folders factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { OfficeFolder } from "le-coffre-resources/dist/Notary";
import type { QueryPayload } from "../../../../types";
import type { IGetFoldersParams } from "../Folders";
import {
	buildFolderArchivePayload,
	buildFoldersCreatePayload,
	buildFoldersUpdatePayload,
	buildEmptyPayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

/**
 * Create GET method for folders
 */
export function createGetMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (q: IGetFoldersParams): Promise<OfficeFolder[]> => {
		const url = new URL(baseUrl);
		baseNotary.appendJsonSearchParams(url, q);
		try {
			return await baseNotary.getRequest<OfficeFolder[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create GET by UID method for folders
 */
export function createGetByUidMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		if (q) baseNotary.appendJsonSearchParams(url, q);
		try {
			return await baseNotary.getRequest<OfficeFolder>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

export type DemandableDocumentTypeApiRow = {
	uid: string;
	name: string | null;
	private_description: string | null;
	retention_days_before_expiry?: number | null;
	default_document_request_recipient?: string | null;
};

/**
 * GET /folders/:uid/demandable-document-types — server-merged types for Ask documents.
 */
export function createGetDemandableDocumentTypesMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<{ document_types: DemandableDocumentTypeApiRow[] }> => {
		const url = new URL(baseUrl.concat(`/${uid}/demandable-document-types`));
		try {
			return await baseNotary.getRequest<{ document_types: DemandableDocumentTypeApiRow[] }>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for folders
 */
export function createPostMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (officeFolder: Partial<OfficeFolder>): Promise<OfficeFolder> => {
		const url = new URL(baseUrl);
		try {
			const payload = buildFoldersCreatePayload(officeFolder);
			return await baseNotary.postRequest(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method for folders
 */
export function createPutMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, body: Partial<OfficeFolder>): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			const payload = buildFoldersUpdatePayload(body);
			return await baseNotary.putRequest(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method for folders
 * Note: Backend performs soft delete, which is allowed even if folder contains customers or documents
 */
export function createDeleteMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseNotary.deleteRequest(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create archive method for folders
 */
export function createArchiveMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, archived_description: string): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}/archive`));
		try {
			const payload = buildFolderArchivePayload(archived_description);
			return await baseNotary.putRequest(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create unarchive method for folders
 */
export function createUnarchiveMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}/unarchive`));
		try {
			return await baseNotary.putRequest(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get deleted method for folders
 */
export function createGetDeletedMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (): Promise<OfficeFolder[]> => {
		const url = new URL(baseUrl.concat("/deleted"));
		try {
			return await baseNotary.getRequest<OfficeFolder[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create restore folder method
 */
export function createRestoreFolderMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}/restore`));
		try {
			return await baseNotary.postRequest(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

export function createPostLongTermArchiveMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<OfficeFolder> => {
		const url = new URL(baseUrl.concat(`/${uid}/long-term-archive`));
		try {
			return await baseNotary.postRequest(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}
